import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Edit, Phone, MapPin, Home } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", house_name: "", phone: "", address: "", notes: "" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => { const { data } = await http.get("/customers"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await http.put(`/customers/${editingId}`, form);
      else await http.post("/customers", form);
      toast.success("Kaydedildi");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch { toast.error("Hata oluştu"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await http.delete(`/customers/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">{items.length} müşteri / ev</div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-customer-button">
              <Plus className="h-4 w-4 mr-2" /> Yeni Müşteri / Ev
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Düzenle" : "Yeni Müşteri / Ev"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Müşteri Adı *</Label><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="customer-name-input" /></div>
              <div><Label>Ev İsmi (Örn: "Bahçelievler Villa")</Label><Input value={form.house_name} onChange={(e) => setForm({...form, house_name: e.target.value})} data-testid="customer-house-input" /></div>
              <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="customer-phone-input" /></div>
              <div><Label>Adres</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} data-testid="customer-address-input" /></div>
              <div><Label>Notlar</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid="customer-notes-input" /></div>
              <DialogFooter><Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-customer-button">Kaydet</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="customers-list">
        {items.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">Henüz müşteri yok</div>}
        {items.map((c) => (
          <Card key={c.id} className="p-5 border-slate-200 hover:shadow-md transition-shadow group">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-xl bg-sky-100 flex items-center justify-center text-sky-700 shrink-0">
                <Home className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 truncate">{c.name}</div>
                {c.house_name && <div className="text-xs text-emerald-700 font-medium">{c.house_name}</div>}
              </div>
            </div>
            <div className="mt-3 space-y-1.5 text-xs text-slate-600">
              {c.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {c.phone}</div>}
              {c.address && <div className="flex items-start gap-1.5"><MapPin className="h-3 w-3 mt-0.5 shrink-0" /> <span className="line-clamp-2">{c.address}</span></div>}
            </div>
            {c.notes && <div className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-3">{c.notes}</div>}
            <div className="mt-3 flex justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => { setForm(c); setEditingId(c.id); setOpen(true); }} data-testid={`edit-customer-${c.id}`}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-customer-${c.id}`}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
